import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import PageTitle from "components/common/PageTitle";
import Button from "components/button/Button";
import Loader from "components/common/Loader";
import Toaster from "components/common/Toaster";
import ReceiptPageView from "components/receiptPage/ReceiptPageView";
import ReceiptPageAdvertisementEditor from "components/receiptPage/ReceiptPageAdvertisementEditor";
import {
  ReceiptPreviewFrame,
  ReceiptSettingsLayout
} from "components/receiptPage/receiptPage.styles";
import Api from "api/Api";
import UserWrapper from "views/Store/user.style";
import {
  appendPromotionalAdvertisements,
  mapAdsForPreview,
  normalizePromotionalAdvertisements
} from "util/storeAdvertisementForm";
import {
  buildPreviewReceiptData,
  defaultReceiptConsentMessage,
  linkFieldByPlatform,
  parseSocialConfig,
  socialPlatformMeta
} from "util/receiptPageDefaults";

const sectionToggles = [
  { key: "feedback", label: "Feedback emojis" },
  { key: "wallet", label: "Google Wallet button" },
  { key: "appStore", label: "App Store badge" },
  { key: "playStore", label: "Play Store badge" }
];

function ReceiptPageSettings(props) {
  const { storeId, isStore, storeName, history, match } = props;
  const resolvedStoreId = isStore
    ? storeId
    : Number(match.params.storeId) || storeId;
  const toaster = useRef();
  const fileInputRef = useRef();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storeRecord, setStoreRecord] = useState(null);
  const [form, setForm] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [
    deletePromotionalAdvertisementList,
    setDeletePromotionalAdvertisementList
  ] = useState([]);

  useEffect(() => {
    if (!resolvedStoreId) {
      if (isStore) history.push("/store");
      return;
    }
    setLoading(true);
    setLoadError(null);
    Api.post("admin/store/storeList", { search: "" })
      .then(res => {
        const list = res.data?.data || [];
        const store = list.find(
          s => Number(s.storeId) === Number(resolvedStoreId)
        );
        if (!store) throw new Error("Store not found");
        setStoreRecord(store);
        setForm({
          name: store.storeName,
          image: store.storeLogo,
          storeDescription: store.storeDescription || "",
          facebookLink: store.facebookLink || "",
          twitterLink: store.twitterLink || "",
          googleLink: store.googleLink || "",
          instagramLink: store.instagramLink || "",
          linkedinLink: store.linkedinLink || "",
          trustpilotLink: store.trustpilotLink || "",
          appstoreLink: store.appstoreLink || "",
          playstoreLink: store.playstoreLink || "",
          primaryColor: store.primaryColor || "#1F3B6F",
          secondaryColor: store.secondaryColor || "#4CAF50",
          receiptTagline: store.receiptTagline || "",
          receiptConsentMessage:
            store.receiptConsentMessage || defaultReceiptConsentMessage,
          receiptPrivacyText: store.receiptPrivacyText || "",
          receiptPrivacyPolicy: store.receiptPrivacyPolicy || "",
          receiptSocialConfig: parseSocialConfig(store.receiptSocialConfig),
          promotionalAdvertisementList: normalizePromotionalAdvertisements(
            store.promotionalAdvertisementList
          )
        });
        setDeletePromotionalAdvertisementList([]);
        setLogoPreview(store.storeLogo);
      })
      .catch(err => {
        const message = err.message || "Failed to load store";
        setLoadError(message);
        if (toaster.current) toaster.current.error(message);
      })
      .finally(() => setLoading(false));
  }, [resolvedStoreId, history, isStore]);

  const previewData = useMemo(() => {
    if (!form) return null;
    return buildPreviewReceiptData({
      ...form,
      image: logoPreview || form.image,
      promotionalAdvertisementList: mapAdsForPreview(
        form.promotionalAdvertisementList
      )
    });
  }, [form, logoPreview]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateSocialToggle = (key, enabled) => {
    setForm(prev => ({
      ...prev,
      receiptSocialConfig: {
        ...parseSocialConfig(prev.receiptSocialConfig),
        [key]: enabled
      }
    }));
  };

  const handleLogoChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const openPreviewTab = () => {
    const base = window.location.origin;
    window.open(`${base}/receipt-preview/${resolvedStoreId}`, "_blank");
  };

  const handleSave = () => {
    if (!storeRecord || !form) return;
    setSaving(true);

    const body = {
      storeId: resolvedStoreId,
      active: storeRecord.active ?? storeRecord.isActive,
      storeLoyaltyPartner:
        storeRecord.storeLoyaltyPartner ?? storeRecord.isStoreLoyaltyPartner,
      storeName: form.name,
      storeDescription: form.storeDescription,
      facebookLink: form.facebookLink,
      twitterLink: form.twitterLink,
      googleLink: form.googleLink,
      instagramLink: form.instagramLink,
      linkedinLink: form.linkedinLink,
      trustpilotLink: form.trustpilotLink,
      appstoreLink: form.appstoreLink,
      playstoreLink: form.playstoreLink,
      primaryColor: form.primaryColor,
      secondaryColor: form.secondaryColor,
      receiptTagline: form.receiptTagline,
      receiptConsentMessage: form.receiptConsentMessage,
      receiptPrivacyText: form.receiptPrivacyText,
      receiptPrivacyPolicy: form.receiptPrivacyPolicy,
      receiptSocialConfig: JSON.stringify(
        parseSocialConfig(form.receiptSocialConfig)
      )
    };

    if (logoFile) {
      body.storeLogo = logoFile;
    }

    const formData = new FormData();
    Object.keys(body).forEach(key => {
      if (body[key] !== undefined && body[key] !== null) {
        formData.append(key, body[key]);
      }
    });
    appendPromotionalAdvertisements(
      formData,
      form.promotionalAdvertisementList || [],
      deletePromotionalAdvertisementList
    );

    Api.put("admin/store/edit", formData)
      .then(res => {
        if (res.data.status) {
          if (toaster.current) toaster.current.success(res.data.message);
          setLogoFile(null);
          setDeletePromotionalAdvertisementList([]);
          Api.post("admin/store/storeList", { search: "" }).then(listRes => {
            const store = (listRes.data?.data || []).find(
              s => Number(s.storeId) === Number(resolvedStoreId)
            );
            if (store) {
              setStoreRecord(store);
              setForm(prev => ({
                ...prev,
                promotionalAdvertisementList: normalizePromotionalAdvertisements(
                  store.promotionalAdvertisementList
                )
              }));
            }
          });
        } else if (toaster.current) {
          toaster.current.error(res.data.message);
        }
      })
      .catch(err => {
        if (toaster.current) toaster.current.error(err.message);
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <UserWrapper {...props}>
        <Loader loading />
      </UserWrapper>
    );
  }

  if (loadError || !form) {
    return (
      <UserWrapper {...props}>
        <PageTitle title="Receipt Page" className="plr-15" />
        <div className="plr-15 pb-30">
          <div className="card-box pa-20 text-center">
            <p className="fs-16 mb-20">
              {loadError || "Unable to load store settings."}
            </p>
            <Button
              className="c-btn c-info c-btn-lg"
              onClick={() =>
                history.push(isStore ? "/store/dashboard" : "/store")
              }
            >
              Back
            </Button>
          </div>
        </div>
        <Toaster ref={toaster} />
      </UserWrapper>
    );
  }

  const socialConfig = parseSocialConfig(form.receiptSocialConfig);

  return (
    <UserWrapper {...props}>
      <PageTitle
        title={
          isStore
            ? `Receipt Page (${storeName})`
            : `Receipt Page — ${form.name}`
        }
        className="plr-15"
        breadCrumb={[
          { name: isStore ? "sidebar.storeDashboard" : "sidebar.dashboard" },
          { name: "Receipt Page" }
        ]}
      />

      <div className="plr-15 pb-30">
        <ReceiptSettingsLayout>
          <div className="card-box pa-20">
            <h3 className="fs-18 bold-text mb-20">Branding &amp; content</h3>

            <div className="form-group">
              <label className="fs-14 medium-text">Store logo</label>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Logo"
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 12,
                      objectFit: "cover"
                    }}
                  />
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleLogoChange}
                />
                <Button
                  className="c-btn c-info"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload logo
                </Button>
              </div>
            </div>

            <div className="form-group">
              <label className="fs-14 medium-text">Receipt tagline</label>
              <input
                className="form-control react-form-input"
                value={form.receiptTagline}
                placeholder="Thanks for shopping with us!"
                onChange={e => updateField("receiptTagline", e.target.value)}
              />
            </div>

            <h4 className="fs-16 bold-text mt-20 mb-12">
              Email consent &amp; privacy
            </h4>
            <p className="text-muted fs-13 mb-12">
              Shown next to the email checkbox on the QR receipt page. Customers
              tap the privacy link to read your policy in a popup.
            </p>
            <div className="form-group">
              <label className="fs-14 medium-text">Consent message</label>
              <textarea
                className="form-control react-form-input"
                rows={3}
                value={form.receiptConsentMessage}
                placeholder={defaultReceiptConsentMessage}
                onChange={e =>
                  updateField("receiptConsentMessage", e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label className="fs-14 medium-text">Privacy link label</label>
              <input
                className="form-control react-form-input"
                value={form.receiptPrivacyText}
                placeholder="Privacy Policy"
                onChange={e =>
                  updateField("receiptPrivacyText", e.target.value)
                }
              />
            </div>
            <div className="form-group">
              <label className="fs-14 medium-text">Privacy policy</label>
              <textarea
                className="form-control react-form-input"
                rows={8}
                value={form.receiptPrivacyPolicy}
                placeholder="Enter your store privacy policy. Customers will see this when they tap the privacy link."
                onChange={e =>
                  updateField("receiptPrivacyPolicy", e.target.value)
                }
              />
            </div>

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div className="form-group" style={{ flex: "1 1 160px" }}>
                <label className="fs-14 medium-text">Primary color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="color"
                    value={form.primaryColor}
                    onChange={e => updateField("primaryColor", e.target.value)}
                  />
                  <input
                    className="form-control react-form-input"
                    value={form.primaryColor}
                    onChange={e => updateField("primaryColor", e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group" style={{ flex: "1 1 160px" }}>
                <label className="fs-14 medium-text">Accent color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="color"
                    value={form.secondaryColor}
                    onChange={e =>
                      updateField("secondaryColor", e.target.value)
                    }
                  />
                  <input
                    className="form-control react-form-input"
                    value={form.secondaryColor}
                    onChange={e =>
                      updateField("secondaryColor", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            <h4 className="fs-16 bold-text mt-20 mb-12">
              Social &amp; review links
            </h4>
            <p className="text-muted fs-13 mb-15">
              Add a URL and toggle visibility on the receipt page.
            </p>

            {socialPlatformMeta.map(platform => {
              const field = linkFieldByPlatform[platform.key];
              return (
                <div
                  key={platform.key}
                  className="form-group"
                  style={{
                    border: "1px solid #e8edf3",
                    borderRadius: 10,
                    padding: 12,
                    marginBottom: 10
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8
                    }}
                  >
                    <label className="fs-14 medium-text mb-0">
                      <i
                        className={platform.icon}
                        style={{ color: platform.color, marginRight: 8 }}
                      />
                      {platform.label}
                    </label>
                    <label className="fs-13 mb-0">
                      <input
                        type="checkbox"
                        checked={socialConfig[platform.key] !== false}
                        onChange={e =>
                          updateSocialToggle(platform.key, e.target.checked)
                        }
                      />{" "}
                      Show
                    </label>
                  </div>
                  <input
                    className="form-control react-form-input"
                    value={form[field] || ""}
                    placeholder={`https://${platform.key}.com/your-page`}
                    onChange={e => updateField(field, e.target.value)}
                  />
                </div>
              );
            })}

            <div className="form-group">
              <label className="fs-14 medium-text">App Store link</label>
              <input
                className="form-control react-form-input"
                value={form.appstoreLink}
                onChange={e => updateField("appstoreLink", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="fs-14 medium-text">Play Store link</label>
              <input
                className="form-control react-form-input"
                value={form.playstoreLink}
                onChange={e => updateField("playstoreLink", e.target.value)}
              />
            </div>

            <ReceiptPageAdvertisementEditor
              ads={form.promotionalAdvertisementList || []}
              deleteIds={deletePromotionalAdvertisementList}
              onDeleteIdsChange={setDeletePromotionalAdvertisementList}
              onChange={list =>
                setForm(prev => ({
                  ...prev,
                  promotionalAdvertisementList: list || []
                }))
              }
            />

            <h4 className="fs-16 bold-text mt-20 mb-12">Sections</h4>
            {sectionToggles.map(item => (
              <label
                key={item.key}
                className="fs-14"
                style={{ display: "block", marginBottom: 8 }}
              >
                <input
                  type="checkbox"
                  checked={socialConfig[item.key] !== false}
                  onChange={e => updateSocialToggle(item.key, e.target.checked)}
                />{" "}
                {item.label}
              </label>
            ))}

            <div
              className="rp-settings-actions"
              style={{
                display: "flex",
                gap: 12,
                marginTop: 24,
                flexWrap: "wrap"
              }}
            >
              <Button
                className="c-btn c-success c-btn-lg"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save settings"}
              </Button>
              <Button
                className="c-btn c-info c-btn-lg"
                onClick={openPreviewTab}
              >
                Open preview
              </Button>
            </div>
          </div>

          <div>
            <h3 className="fs-16 bold-text mb-12 text-center">Live preview</h3>
            <ReceiptPreviewFrame>
              <ReceiptPageView receiptData={previewData} previewMode embedded />
            </ReceiptPreviewFrame>
          </div>
        </ReceiptSettingsLayout>
      </div>
      <Toaster ref={toaster} />
    </UserWrapper>
  );
}

const mapStateToProps = state => ({
  ...state.themeChanger,
  ...state.storeID,
  adminType: state.auth.adminType
});

export default compose(
  connect(mapStateToProps),
  withRouter
)(ReceiptPageSettings);
